'use client'

import React from 'react'
import { Box, IconButton, keyframes, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Close, LocalOffer, OpenInNew } from '@mui/icons-material'

import { MediaPlayer } from './mediaPlayer'
import type { MediaFloaterPreviewProps } from './types'

const progressAnimation = keyframes`
    from { width: 0%; }
    to { width: 100%; }
`

/**
 * MediaFloater — floating preview card with overlaid content.
 * Clicking anywhere on the card opens the full-screen view.
 *
 * Overlay elements (all configurable, hidden when not set):
 *   badge    — top-left orange discount chip  (e.g. "49% off")
 *   caption  — bold white heading in bottom gradient
 *   location — smaller white text below caption
 *
 * "✦ Aide" AI branding is always shown bottom-right.
 */
const MediaFloater: React.FC<MediaFloaterPreviewProps> = ({
    mediaItems,
    currentIndex,
    currentDurationMs,
    onOpen,
    onClose,
    onMediaAdvance,
    onVideoDurationChange,
    exploreOffersUrl,
    replayNonce
}) => {
    const currentItem = mediaItems[currentIndex]
    const hasBottomOverlay = Boolean(currentItem?.caption || currentItem?.location || exploreOffersUrl)

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: { xs: 82, md: 20 },
                right: { xs: 16, md: 20 },
                zIndex: 999,
                width: { xs: 250, md: 300 },
                pointerEvents: 'none'
            }}
        >
            {/* ── Media card ── */}
            <Box
                onClick={onOpen}
                sx={{
                    width: '100%',
                    height: { xs: 360, md: 405 },
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: theme => `0 10px 30px ${alpha(theme.palette.common.black, 0.35)}`,
                    bgcolor: 'common.black',
                    position: 'relative',
                    transition: 'transform 0.2s ease-in-out',
                    pointerEvents: 'auto',
                    '&:hover': { transform: 'scale(1.02)' }
                }}
            >
                {/* Progress bars */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        right: 8,
                        display: 'flex',
                        gap: 0.5,
                        zIndex: 4
                    }}
                >
                    {mediaItems.map((item, index) => {
                        const isActive = index === currentIndex
                        const barDurationMs = isActive ? currentDurationMs : item.duration ?? 3000
                        return (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    height: 2,
                                    bgcolor: theme => alpha(theme.palette.common.white, 0.35),
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                            >
                                <Box
                                    key={isActive ? `active-${currentIndex}-${currentDurationMs}` : `inactive-${index}`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: '100%',
                                        bgcolor: 'common.white',
                                        borderRadius: 1,
                                        ...(index < currentIndex
                                            ? { width: '100%' }
                                            : isActive
                                              ? { width: '0%', animation: `${progressAnimation} ${barDurationMs}ms linear forwards` }
                                              : { width: '0%' })
                                    }}
                                />
                            </Box>
                        )
                    })}
                </Box>

                {/* Media player — fills entire card */}
                <Box sx={{ width: '100%', height: '100%' }}>
                    <MediaPlayer
                        key={replayNonce}
                        mediaItem={currentItem}
                        onNext={onMediaAdvance}
                        autoPlay={true}
                        isInModal={false}
                        priority={currentIndex === 0}
                        onDurationChange={onVideoDurationChange}
                    />
                </Box>

                {/* Close button — top-right */}
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        onClose()
                    }}
                    sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 26,
                        height: 26,
                        bgcolor: theme => alpha(theme.palette.common.black, 0.45),
                        color: 'common.white',
                        backdropFilter: 'blur(4px)',
                        pointerEvents: 'auto',
                        zIndex: 5,
                        '&:hover': { bgcolor: theme => alpha(theme.palette.common.black, 0.65) }
                    }}
                    size="small"
                >
                    <Close sx={{ fontSize: 13 }} />
                </IconButton>

                {/* Discount badge — top-left, hidden when badge is not set */}
                {currentItem?.badge && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 22,
                            left: 10,
                            zIndex: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            bgcolor: '#FF6B00',
                            color: 'common.white',
                            borderRadius: '6px',
                            px: 0.75,
                            py: 0.3
                        }}
                    >
                        <LocalOffer sx={{ fontSize: 10 }} />
                        <Typography
                            sx={{
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                lineHeight: 1,
                                letterSpacing: 0.2
                            }}
                        >
                            {currentItem.badge}
                        </Typography>
                    </Box>
                )}

                {/* Bottom gradient overlay — caption + location */}
                {hasBottomOverlay && (
                    <Box
                        // Inline style: keeps the vertical gradient out of emotion's RTL plugin,
                        // which flips color-stop percentages and over-darkens the card in Arabic.
                        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)' }}
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 3,
                            px: 1.25,
                            pt: 4,
                            pb: 1.5
                        }}
                    >
                        {currentItem?.caption && (
                            <Typography
                                sx={{
                                    color: 'common.white',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.8rem', md: '0.85rem' },
                                    lineHeight: 1.3,
                                    mb: currentItem?.location ? 0.4 : 0,
                                    // reserve space so "Aide" badge doesn't overlap long text
                                    pr: 4
                                }}
                            >
                                {currentItem.caption}
                            </Typography>
                        )}
                        {currentItem?.location && (
                            <Typography
                                sx={{
                                    color: theme => alpha(theme.palette.common.white, 0.85),
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2
                                }}
                            >
                                {currentItem.location}
                            </Typography>
                        )}

                        {/* Explore all link — links to the full reels page */}
                        {exploreOffersUrl && (
                            <Box
                                component="a"
                                href={exploreOffersUrl}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.3,
                                    mt: 0.75,
                                    color: theme => alpha(theme.palette.common.white, 0.9),
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    letterSpacing: 0.2,
                                    '&:hover': { color: 'common.white' }
                                }}
                            >
                                Explore all offers
                                <OpenInNew sx={{ fontSize: 10 }} />
                            </Box>
                        )}
                    </Box>
                )}

            </Box>
        </Box>
    )
}

export { MediaFloater, progressAnimation }
export type { MediaFloaterPreviewProps }
